import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../../assets/pictures/logo.png';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Parse the ?redirect= query param
  const redirectPath = new URLSearchParams(location.search).get('redirect');

  // If user is already logged in, send them straight to destination
  useEffect(() => {
    if (!user) return;
    if (user.role === 'student' && redirectPath) {
      navigate(redirectPath, { replace: true });
    } else if (user.role === 'student') {
      navigate('/dashboard', { replace: true });
    } else if (user.role === 'super_admin') {
      navigate('/admin', { replace: true });
    } else if (user.role === 'counselor') {
      navigate('/counselor', { replace: true });
    } else if (user.role === 'agent') {
      navigate('/agent', { replace: true });
    } else if (user.role === 'rep1' || user.role === 'rep2') {
      navigate('/representative', { replace: true });
    } else if (user.role === 'rep3') {
      navigate('/partner', { replace: true });
    }
  }, [user]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');

      // Redirect based on role; honour ?redirect= for students
      if (user.role === 'student') {
        navigate(redirectPath || '/dashboard');
      } else if (user.role === 'super_admin') {
        navigate('/admin');
      } else if (user.role === 'counselor') {
        navigate('/counselor');
      } else if (user.role === 'agent') {
        navigate('/agent');
      } else if (user.role === 'rep1' || user.role === 'rep2') {
        navigate('/representative');
      } else if (user.role === 'rep3') {
        navigate('/partner');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <a href="https://www.fly8.global/" className="flex items-center">
              <img src={logo} alt="Fly8 Logo" className="h-16 w-auto" />
            </a>
            <Link to="/signup">
              <Button
                variant="default"
                className="bg-[#1B86F0] hover:bg-[#1574d0] text-white rounded-full px-6"
              >
                Register
              </Button>
            </Link>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-[#1B86F0] via-[#22A3EA] to-[#1574d0]" />
      </header>

      <div className="flex-1 flex">
        {/* Left Side - Welcome Text with Background */}
        <div
          className="hidden lg:flex flex-1 relative items-center justify-center p-12"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 opacity-75 bg-gradient-to-br from-[#EAF6FF]/95 via-[#E0F2FF]/90 to-[#D6ECFF]/85" />

          {/* Content */}
          <div className="relative z-10 max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-outfit font-bold text-gray-900 mb-6">
              <span className="text-[#1B86F0]">Login</span> to Your Account
            </h1>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Welcome to the Fly8 portal!
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Here, you can access all the tools and resources you need to make
              your study abroad journey a success. Join our global community and
              unlock endless possibilities. Log in now and embark on your
              educational adventure with Fly8!
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-zinc-50">
          <div className="w-full max-w-md">
            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-outfit font-bold text-[#1B86F0]">
                  Login
                </h2>
                <p className="text-gray-500 mt-2 text-sm">
                  Your ultimate guide to studying abroad.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="login-form"
              >
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Email ID <span className="text-[#1B86F0]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="h-12 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-gray-700 font-medium"
                  >
                    Password <span className="text-[#1B86F0]">*</span>
                  </Label>

                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={e =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                      className="h-12 pr-10 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                      data-testid="login-password-input"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                      className="border-gray-300 data-[state=checked]:bg-[#1B86F0] data-[state=checked]:border-[#1B86F0]"
                    />
                    <Label
                      htmlFor="remember"
                      className="text-sm text-gray-600 font-normal cursor-pointer"
                    >
                      Remember me
                    </Label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#1B86F0] hover:text-[#1574d0] font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#1B86F0] hover:bg-[#1574d0] text-white font-semibold rounded-full shadow-lg shadow-[#1B86F0]/25 transition-all duration-200"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>

              <div className="text-center pt-2">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link
                    to="/signup"
                    className="text-[#1B86F0] hover:text-[#1574d0] font-semibold transition-colors"
                  >
                    Sign up here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1B86F0] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-white text-sm">
            Copyright © {new Date().getFullYear()} Fly8. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
